import SimpleModal from "app/components/Modals/SimpleModal";
import Select from "react-select";
import { MultiValue } from "react-select";
import Button from "app/storybookComponents/Button";
import {
  faCirclePlus,
  faCircleXmark,
} from "@fortawesome/pro-regular-svg-icons";
import SimpleModalFooter from "app/components/Modals/SimpleModalFooter";
import { useEffect, useMemo, useState } from "react";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getUserName } from "utils/helperFunctions";
import {
  getHarmDetectionAlertRecipients,
  selectHarmDetectionAlertRecipients,
  selectUpdateHarmDetectionAlertRecipientsStatus,
  selectWeeklyCheckInCompanySettings,
  updateHarmDetectionAlertRecipients,
  updateWeeklyCheckInCompanySettings,
  selectUpdateWeeklyCheckInCompanySettingsStatus,
} from "../slice";
import AvatarCircle from "app/components/AvatarCircle";
import { getMemberOption } from "app/components/GlobalNavbar/helpers";

interface Props {
  show: boolean;
  onHide: () => void;
}

interface AdminOption {
  label: string;
  value: string; // String(userAccountId)
  name: string;
}

const HarmDetectionRecipientsModal = ({ show, onHide }: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const existingRecipients = useAppSelector(selectHarmDetectionAlertRecipients);
  const companySettings = useAppSelector(selectWeeklyCheckInCompanySettings);
  const updateRecipientsStatus = useAppSelector(
    selectUpdateHarmDetectionAlertRecipientsStatus
  );
  const updateSettingsStatus = useAppSelector(
    selectUpdateWeeklyCheckInCompanySettingsStatus
  );

  const [selectedUserAccountIds, setSelectedUserAccountIds] = useState<{
    [key: number]: true;
  }>({});
  const [showAddAdminButton, setShowAddAdminButton] = useState(false);
  const [selectInputValue, setSelectInputValue] = useState("");

  // Seed from Redux when the modal opens
  useEffect(() => {
    if (!show) return;
    dispatch(getHarmDetectionAlertRecipients());
  }, [show, dispatch]);

  useEffect(() => {
    const seeded: { [key: number]: true } = {};
    existingRecipients.forEach((id) => {
      seeded[id] = true;
    });
    setSelectedUserAccountIds(seeded);
  }, [existingRecipients]);

  useEffect(() => {
    if (!Object.keys(selectedUserAccountIds).length) {
      setShowAddAdminButton(true);
    }
  }, [selectedUserAccountIds]);

  // Admins only — the API validates this server-side too
  const allAdminOptions = useMemo((): AdminOption[] => {
    return Object.values(usersById)
      .filter((user) => user.tmgRoleId === 1)
      .map((user) => ({
        label: user.emailAddress,
        value: String(user.userAccountId),
        name: `${user.firstName} ${user.lastName}`,
      }));
  }, [usersById]);

  const filteredOptions = useMemo((): AdminOption[] => {
    if (!selectInputValue) return [];
    const lower = selectInputValue.toLowerCase();
    return allAdminOptions
      .filter(
        (o) =>
          o.label.toLowerCase().includes(lower) ||
          o.name.toLowerCase().includes(lower)
      )
      .slice(0, 50);
  }, [selectInputValue, allAdminOptions]);

  const getDropdownOptions = (userAccountIds: string[]): AdminOption[] => {
    return userAccountIds.map(Number).reduce<AdminOption[]>((acc, id) => {
      const user = usersById[id];
      if (!user) return acc;
      acc.push({
        label: user.emailAddress,
        value: String(user.userAccountId),
        name: `${user.firstName} ${user.lastName}`,
      });
      return acc;
    }, []);
  };

  const handleSelectChange = (values: MultiValue<AdminOption>) => {
    const updated: { [key: number]: true } = {};
    values?.forEach((option) => {
      updated[Number(option.value)] = true;
    });
    setSelectedUserAccountIds(updated);
    setShowAddAdminButton(Object.keys(updated).length === 0);
  };

  const onAddAllAdmins = () => {
    setSelectedUserAccountIds((prev) => {
      const updated = { ...prev };
      allAdminOptions.forEach((o) => {
        updated[Number(o.value)] = true;
      });
      return updated;
    });
    setShowAddAdminButton(false);
  };

  const onRemoveAllAdmins = () => {
    setSelectedUserAccountIds({});
    setShowAddAdminButton(true);
  };

  const formatOptionLabel = (option: AdminOption) => {
    const userAccountId = Number(option.value);
    if (selectedUserAccountIds[userAccountId]) {
      const user = usersById[userAccountId];
      return <div>{getUserName(user)}</div>;
    }
    return getMemberOption({
      label: option.label,
      avatarCircle: (
        <AvatarCircle
          name={option.label}
          userAccountId={userAccountId}
          size="small"
        />
      ),
    });
  };

  const onSubmit = async () => {
    const userAccountIds = Object.keys(selectedUserAccountIds).map(Number);
    await dispatch(updateHarmDetectionAlertRecipients({ userAccountIds }));
    if (companySettings?.harmDetectionNotificationMode !== "specific_admins") {
      await dispatch(
        updateWeeklyCheckInCompanySettings({
          harmDetectionNotificationMode: "specific_admins",
        })
      );
    }
    onHide();
  };

  const isLoading =
    updateRecipientsStatus === "loading" || updateSettingsStatus === "loading";

  const selectedCount = Object.keys(selectedUserAccountIds).length;

  return (
    <SimpleModal
      show={show}
      onHide={onHide}
      title="Harm Detection Alert Recipients"
    >
      <p>
        Select which admins will receive harm detection alert emails. Only org
        admins can be added to this list.
      </p>
      <hr className="m-0" />
      <p>Choose specific admins to receive alerts:</p>
      <Select<AdminOption, true>
        placeholder="Search admins by name or email..."
        options={filteredOptions}
        isMulti
        inputValue={selectInputValue}
        onInputChange={(val) => setSelectInputValue(val)}
        value={getDropdownOptions(Object.keys(selectedUserAccountIds))}
        onChange={handleSelectChange}
        formatOptionLabel={formatOptionLabel}
        filterOption={() => true}
        noOptionsMessage={() =>
          selectInputValue ? "No matching admins found" : null
        }
        classNames={{
          multiValue: () => "multi-value user-option-white",
          option: () => "option-value-with-border-bottom option",
        }}
      />
      <div>
        {showAddAdminButton ? (
          <Button
            onClick={onAddAllAdmins}
            variant="secondary-blue"
            className="border-0"
          >
            <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
            Add all admins
          </Button>
        ) : (
          <Button
            onClick={onRemoveAllAdmins}
            variant="secondary-gray"
            className="border-0"
          >
            <FontAwesomeIcon icon={faCircleXmark} className="me-2" />
            Remove all admins
          </Button>
        )}
      </div>
      <SimpleModalFooter
        onPrimaryButtonClick={onSubmit}
        primaryButtonText="Save"
        isPrimaryButtonDisabled={selectedCount === 0 || isLoading}
        onBackButtonClick={onHide}
        backButtonText="Back"
      />
    </SimpleModal>
  );
};

export default HarmDetectionRecipientsModal;
